import { Fragment, useContext } from "react"

// Router
import { useParams } from "@/lib/router"

// Icons
import {
	FiDownload,
	FiPaperclip,
	FiCalendar,
	FiHash,
	FiMapPin,
} from "@/lib/icons"

// Forms
import { useFormik, FormikProvider } from "formik"
import { FormGroup } from "@/components/form-controls/FormGroup"
import { FormikSubmitButton } from "@/components/form-controls/formik"

// Utils
import { transformErrorToText } from "@/api/rest/utils"

// Constants
import { dateFormat, UserRoles } from "@/constants/constants"

// Context
import { useCurrentUserRoles } from "@/context/user"
import { RedirectContext } from "@/context/redirectContext"

// UI
import { cn } from "@/lib/utils"
import { Button } from "@/components/Button"
import { CardWrapper, CardBody, CardFooter, Card } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { useToasts } from "@/context/toasts"

// GraphQL
import { apiV1PaymentOwnerVerificationCreate } from "@/api/rest/generated/api/api"
import {
	usePaymentDetailProjectOwnerQuery,
	PaymentStateEnum,
} from "@/api/graphql"

// Types
import { ActionEnum } from "@/api/rest/generated/@types"

import { DateTime } from "@/lib/dates"

// Translations
import { useLang } from "@/context/lang"
import { Trans, useTrans } from "@/i18n"

// Tables
import {
	PaymentOverviewForProjectOwner,
	PaymentOverviewForProjectOwnerLoading,
} from "./components/PaymentOverviewForProjectOwner"

/**
 * @returns PaymentDetailProjectOwner
 */
export function PaymentDetailProjectOwner() {
	// Translations
	const t = useTrans("payments")

	// Get data
	const { id: interestPaymentId } = useParams()
	const { data, refetch, isLoading } = usePaymentDetailProjectOwnerQuery({
		id: interestPaymentId,
	})
	const interestPayment = data?.interest_payments?.results?.[0]

	// Context
	const { redirect } = useContext(RedirectContext)

	// Loading state
	if (isLoading) {
		return <PaymentDetailProjectOwnerLoading />
	}

	// Data state
	return (
		<div className="space-y-6 md:space-y-8" data-id={interestPaymentId}>
			<Card>
				<div className="md:flex md:space-x-3">
					<Heading as="h4" className="mb-3 mr-auto sm:truncate">
						{t("payments:payments.copy.title")}
					</Heading>
					<div className="flex space-x-3">
						<Button
							variant="primary"
							onClick={() => {
								redirect(
									data?.interest_payments?.results?.[0]
										?.download_pdf_url || "",
								)
							}}
						>
							<FiDownload className="mr-2" />
							{t("payments:payments.pdf.button")}
						</Button>
						<Button
							variant="primary"
							onClick={() => {
								redirect(
									data?.interest_payments?.results?.[0]
										?.download_csv_url || "",
								)
							}}
						>
							<FiDownload className="mr-2" />
							{t("payments:payments.csv.button")}
						</Button>
					</div>
				</div>
				{t("payments:payments.copy.owner", {
					name: data?.me?.first_name,
				})
					.split("\n\n")
					.map((text, index) => (
						<p
							key={`${index}.${text}`}
							className="mt-4 text-sm text-gray-700"
						>
							{text.split("\n").map((text, index) => (
								<Fragment key={`${index}.${text}`}>
									{text}
									<br />
								</Fragment>
							))}
						</p>
					))}
				<dt className="mt-8 text-sm font-medium text-gray-500">
					{t("payments:payments.info_list.attachments.heading")}
				</dt>
				<dd className="mt-1 text-sm text-gray-700">
					<ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
						<DownloadPDF />
						<DownloadCSV />
					</ul>
				</dd>
			</Card>

			{/** Payment info list */}
			<PaymentInfoList />
			<CardWrapper>
				<PaymentOverviewForProjectOwner />
			</CardWrapper>

			{/** Show if pending supplier */}
			{interestPayment?.state === PaymentStateEnum.PendingOwner ? (
				<PaymentDetailConfirmation refetch={refetch} />
			) : (
				<CardWrapper>
					<CardBody>
						<Heading as="h3" className="mb-3">
							{t("payments:payments.confirmation.finished.date", {
								date: DateTime.fromISO(
									interestPayment?.project_owner_acceptance_date_time,
								).toFormat(dateFormat),
							})}
						</Heading>
						{t("payments:payments.confirmation.finished.title")}
					</CardBody>
				</CardWrapper>
			)}
		</div>
	)
}

function DownloadPDF() {
	// Translations
	const t = useTrans("payments")

	const { id: interestPaymentId } = useParams()
	const { data } = usePaymentDetailProjectOwnerQuery({
		id: interestPaymentId,
	})

	if (!data?.interest_payments?.results?.[0]?.download_pdf_url) return null

	return (
		<a
			href={data.interest_payments.results[0]?.download_pdf_url}
			target="_blank"
			rel="noreferrer"
			className="group flex w-full items-center justify-between py-3 pl-3 pr-4 text-sm hover:bg-gray-100"
			data-testid="downloadpdf"
		>
			<div className="flex items-center">
				<FiPaperclip
					className="size-5 shrink-0 text-gray-400"
					aria-hidden="true"
				/>
				<span className="ml-2 flex-1 truncate">
					{t("payments:payments.info_list.attachments.pdf.cta", {
						name: data.interest_payments.results[0]?.project?.name,
					})}
				</span>
			</div>
			<div className="ml-4 font-medium text-secondary-300 group-hover:text-secondary-700">
				{t("payments:payments.info_list.attachments.pdf.download")}
			</div>
		</a>
	)
}

function DownloadCSV() {
	// Translations
	const t = useTrans("payments")

	// Data
	const { id: interestPaymentId } = useParams()
	const { data } = usePaymentDetailProjectOwnerQuery({
		id: interestPaymentId,
	})

	if (!data?.interest_payments?.results?.[0]?.download_csv_url) return null

	return (
		<a
			href={data.interest_payments.results[0]?.download_csv_url}
			target="_blank"
			rel="noreferrer"
			className="group flex w-full items-center justify-between py-3 pl-3 pr-4 text-sm hover:bg-gray-100"
			data-testid="downloadcsv"
		>
			<div className="flex items-center">
				<FiPaperclip
					className="size-5 shrink-0 text-gray-400"
					aria-hidden="true"
				/>
				<span className="ml-2 flex-1 truncate">
					{t("payments:payments.info_list.attachments.csv.cta", {
						name: data.interest_payments.results[0]?.project?.name,
					})}
				</span>
			</div>
			<div className="ml-4 font-medium text-secondary-300 group-hover:text-secondary-700">
				{t("payments:payments.info_list.attachments.csv.download")}
			</div>
		</a>
	)
}

/**
 * PaymentInfoList
 * @returns
 */
function PaymentInfoList() {
	// I18n
	const { formatCurrency, formatDate, formatNumber } = useLang()
	const t = useTrans("payments")

	// Data
	const { id: interestPaymentId } = useParams()
	const { data } = usePaymentDetailProjectOwnerQuery({
		id: interestPaymentId,
	})

	const interestPayment = data?.interest_payments?.results?.[0]

	return (
		<>
			<title>
				{t("common:common.web.title", {
					page: t("payments:payments.info_list.title", {
						name: interestPayment?.project?.name,
					}),
				})}
			</title>
			<CardWrapper className="bg-white">
				<CardBody className="border-b border-gray-200">
					<div className="flex justify-between">
						<Heading as="h3" className="mb-1">
							{t("payments:payments.info_list.header", {
								name: interestPayment?.project?.name,
							})}
						</Heading>
					</div>
					<div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8">
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<FiCalendar
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							<span className="text-gray-400">
								<Trans
									ns="payments"
									i18nKey="payments:payments.info_list.date"
									values={{
										from: DateTime.fromISO(
											interestPayment?.interestperiod
												?.start,
										).toFormat(
											t(
												"payments:payments.info_list.date.date_format",
											),
										),
										to: DateTime.fromISO(
											interestPayment?.interestperiod
												?.end,
										).toFormat(
											t(
												"payments:payments.info_list.date.date_format",
											),
										),
									}}
									components={{
										strong: (
											<span className="font-medium" />
										),
									}}
								/>
							</span>
						</div>

						<div className="mt-2 flex items-center text-sm text-gray-500">
							<FiHash
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							{t("payments:payments.info_list.transaction_id", {
								id: interestPayment?.transaction_id,
							})}
						</div>

						<div className="mt-2 flex items-center text-sm text-gray-500">
							<FiMapPin
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							{t("payments:payments.info_list.location", {
								location: interestPayment?.project?.city,
							})}
						</div>
					</div>
				</CardBody>
				<dl>
					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t("payments:payments.info_list.tariff.title")}
						</dt>
						<dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{/* cost_kwh is in cents */}
							{interestPayment?.interestperiod?.cost_kwh
								? formatCurrency(
										interestPayment.interestperiod
											.cost_kwh / 100,
										{
											maximumFractionDigits: 4,
										},
									)
								: 0}
						</dd>
					</div>
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.rental_period_from_until.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{formatDate(
								DateTime.fromISO(
									interestPayment?.interestperiod?.start,
								),
								{ variant: "verbose" },
							)}
							{" / "}
							{formatDate(
								DateTime.fromISO(
									interestPayment?.interestperiod?.end,
								),
								{ variant: "verbose" },
							)}
						</dd>
					</div>

					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.number_of_shares.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{interestPayment?.project?.total_shares
								? formatNumber(
										interestPayment.project.total_shares,
									)
								: 0}
						</dd>
					</div>
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.total_production.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{t(
								"payments:payments.info_list.total_production.copy",
								{
									number: interestPayment?.total_production
										? formatNumber(
												interestPayment.total_production,
											)
										: 0,
								},
							)}
						</dd>
					</div>

					{/** Zonnedelers production */}
					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.total_production_investor.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{t(
								"payments:payments.info_list.total_production_investor.copy",
								{
									number: interestPayment?.total_production_investor
										? formatNumber(
												parseFloat(
													interestPayment.total_production_investor,
												),
											)
										: 0,
								},
							)}
						</dd>
					</div>

					{/** zonnedelers production limited */}
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.total_production_investor_limited.title",
								{
									interest_per_share_limit_applied:
										interestPayment?.interest_per_share_limit_applied,
								},
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{t(
								"payments:payments.info_list.total_production_investor.copy",
								{
									number: interestPayment?.total_production_investor_limited
										? formatNumber(
												parseFloat(
													interestPayment.total_production_investor_limited,
												),
											)
										: 0,
								},
							)}
						</dd>
					</div>

					{/** Aflossing / amortization */}
					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.total_amortization.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{formatCurrency(
								interestPayment?.interestperiod?.amortization
									? interestPayment.interestperiod
											.amortization
									: 0,
							)}
						</dd>
					</div>
					{/** Rente / interest_amount */}
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.total_interest.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{formatCurrency(
								interestPayment?.interest_amount
									? parseFloat(
											interestPayment?.interest_amount,
										)
									: 0,
							)}
						</dd>
					</div>
					{/** Afrekening / repayment */}
					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.total_repayment.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{formatCurrency(
								interestPayment?.interestperiod?.repayment
									? interestPayment.interestperiod.repayment
									: 0,
							)}
						</dd>
					</div>
					{/** Total */}
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t("payments:payments.info_list.total_sum.title")}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{formatCurrency(
								(interestPayment?.interestperiod?.amortization
									? interestPayment.interestperiod
											.amortization
									: 0) +
									(interestPayment?.interest_amount
										? parseFloat(
												interestPayment?.interest_amount,
											)
										: 0) +
									(interestPayment?.interestperiod?.repayment
										? interestPayment.interestperiod
												.repayment
										: 0),
							)}
						</dd>
					</div>
				</dl>
			</CardWrapper>
		</>
	)
}

function PaymentDetailConfirmation({ refetch }: { refetch: Function }) {
	const { userRoles } = useCurrentUserRoles()
	const t = useTrans("payments")
	const { id } = useParams()
	const toasts = useToasts()

	const form = useFormik({
		initialValues: { status: "" },
		onSubmit: async (values, helpers) => {
			helpers.setStatus("")
			try {
				// Project owner
				if (userRoles.has(UserRoles.projectOwner)) {
					const response = await apiV1PaymentOwnerVerificationCreate(
						Number(id),
						{
							action: values.status as ActionEnum,
						},
					)

					// When payment has been confirmed
					if (response.ok === true) {
						toasts.addToast({
							variant: "success",
							id: `handleApiV1PaymentOwnerVerificationCreate-${Date.now()}`,
							text: t(
								"payments:payments.confirmation.success.accept",
							),
						})
						refetch()
					}
				}
			} catch (e) {
				helpers.setStatus(
					transformErrorToText(e, {
						filteredErrorMessages: new Set("insufficient_rights"),
					}),
				)
			}
		},
	})

	const hasErrorStatus =
		Boolean(form.status) && typeof form.status === "string"

	return (
		<FormikProvider value={form}>
			<form onSubmit={form.handleSubmit}>
				<CardWrapper>
					<CardBody>
						<Heading as="h3" className="mb-3">
							{t("payments:payments.confirmation.title")}
						</Heading>
						<FormGroup
							heading={t("payments:payments.confirmation.copy")}
						>
							<div className="space-y-6 sm:space-y-5">
								<div>
									<p className="text-sm text-gray-500">
										{t(
											"payments:payments.confirmation.approve.copy",
										)}
									</p>
								</div>
							</div>
						</FormGroup>
					</CardBody>
					<CardFooter className="text-right">
						<div className="flex items-center justify-end space-x-3">
							{hasErrorStatus && (
								<p className="mr-4 font-medium text-red-500">
									{t(
										`payments.confirmation.form.error.${form.status}`,
									)}
								</p>
							)}
							<FormikSubmitButton
								disableWhenPristine={false}
								onClick={() =>
									void form.setFieldValue("status", "accept")
								}
								variant={"primary"}
							>
								{t("payments:payments.confirmation.form.send")}
							</FormikSubmitButton>
						</div>
					</CardFooter>
				</CardWrapper>
			</form>
		</FormikProvider>
	)
}

/**
 * PaymentDetailProjectOwnerLoading
 * @returns
 */
export function PaymentDetailProjectOwnerLoading() {
	const t = useTrans("payments")

	return (
		<div className="space-y-6 md:space-y-8">
			<Card>
				<div className="md:flex md:space-x-3">
					<Heading as="h4" className="mb-3 mr-auto sm:truncate">
						<div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
					</Heading>
					<div className="flex space-x-3">
						<div className="h-10 w-32 animate-pulse rounded bg-gray-200" />
						<div className="h-10 w-32 animate-pulse rounded bg-gray-200" />
					</div>
				</div>
				<div className="mt-4 space-y-4">
					<div className="h-4 w-full animate-pulse rounded bg-gray-200" />
					<div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
					<div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
				</div>
				<dt className="mt-8 text-sm font-medium text-gray-500">
					{t("payments:payments.info_list.attachments.heading")}
				</dt>
				<dd className="mt-1">
					<ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
						<li className="group flex w-full items-center justify-between py-3 pl-3 pr-4 text-sm">
							<div className="flex items-center">
								<div className="size-5 animate-pulse rounded bg-gray-200" />
								<div className="ml-2 h-4 w-48 animate-pulse rounded bg-gray-200" />
							</div>
							<div className="ml-4 h-4 w-24 animate-pulse rounded bg-gray-200" />
						</li>
						<li className="group flex w-full items-center justify-between py-3 pl-3 pr-4 text-sm">
							<div className="flex items-center">
								<div className="size-5 animate-pulse rounded bg-gray-200" />
								<div className="ml-2 h-4 w-48 animate-pulse rounded bg-gray-200" />
							</div>
							<div className="ml-4 h-4 w-24 animate-pulse rounded bg-gray-200" />
						</li>
					</ul>
				</dd>
			</Card>

			<CardWrapper className="bg-white">
				<CardBody className="border-b border-gray-200">
					<div className="flex justify-between">
						<Heading as="h3" className="mb-1">
							<div className="h-6 w-64 animate-pulse rounded bg-gray-200" />
						</Heading>
					</div>
					<div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8">
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<div className="mr-1.5 size-5 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
						</div>
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<div className="mr-1.5 size-5 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
						</div>
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<div className="mr-1.5 size-5 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
						</div>
					</div>
				</CardBody>
				<dl>
					<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
						<dt className="text-sm text-gray-500">
							{t("payments:payments.info_list.tariff.title")}
						</dt>
						<dd className="mt-1 sm:col-span-2 sm:mt-0">
							<div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
						</dd>
					</div>
					<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
						<dt className="text-sm text-gray-500">
							{t(
								"payments:payments.info_list.rental_period_from_until.title",
							)}
						</dt>
						<dd className="sm:col-span-2 sm:mt-0">
							<div className="h-4 w-64 animate-pulse rounded bg-gray-200" />
						</dd>
					</div>
				</dl>
			</CardWrapper>

			<CardWrapper>
				<PaymentOverviewForProjectOwnerLoading />
			</CardWrapper>

			<CardWrapper>
				<CardBody>
					<Heading as="h3" className="mb-3">
						<div className="h-6 w-64 animate-pulse rounded bg-gray-200" />
					</Heading>
					<div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
				</CardBody>
			</CardWrapper>
		</div>
	)
}
